import { useState, useEffect, useCallback, useMemo } from "react";
import Swal from "sweetalert2";
import { supabase } from "@/lib/supabaseClient";
import GalleryModal, { type GalleryForm } from "@/pages/admin/website/gallery/components/GalleryModal";

/* ─── Types ──────────────────────────────────────────────────── */
export type GalleryRow = {
  id: string;
  tenant_id: string;
  title: string | null;
  image_url: string;
  category: string | null;
  is_active: boolean;
  sort_order: number;
  created_at: string;
};

interface Props { tenantId: string; }

export default function GalleryTab({ tenantId }: Props) {
  const [items, setItems]         = useState<GalleryRow[]>([]);
  const [loading, setLoading]     = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing]     = useState<GalleryRow | null>(null);
  const [saving, setSaving]       = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [movingId, setMovingId]     = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState("all");

  /* ── Fetch ── */
  const fetchItems = useCallback(async () => {
    const { data } = await supabase
      .from("tenant_gallery_items")
      .select("*")
      .eq("tenant_id", tenantId)
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: true });
    setItems((data as GalleryRow[]) ?? []);
    setLoading(false);
  }, [tenantId]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  /* ── Categories ── */
  const categories = useMemo(() => {
    const cats = new Set(items.map((i) => i.category).filter(Boolean) as string[]);
    return Array.from(cats);
  }, [items]);

  const filtered = useMemo(
    () => activeFilter === "all" ? items : items.filter((i) => i.category === activeFilter),
    [items, activeFilter],
  );

  /* ── Modal open/close ── */
  const openNew  = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (item: GalleryRow) => { setEditing(item); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setEditing(null); };

  /* ── Save (create / update) ── */
  const handleSave = async (form: GalleryForm) => {
    setSaving(true);
    const payload = {
      tenant_id: tenantId,
      image_url: form.image_url.trim(),
      title:     form.title.trim()    || null,
      category:  form.category.trim() || null,
      is_active: form.is_active,
    };

    if (editing) {
      const { error } = await supabase
        .from("tenant_gallery_items")
        .update(payload)
        .eq("id", editing.id)
        .eq("tenant_id", tenantId);
      if (error) {
        setSaving(false);
        Swal.fire({ title: "Error", text: error.message, icon: "error", background: "#ffffff", color: "#0f172a" });
        return;
      }
    } else {
      const maxOrder = items.reduce((m, i) => Math.max(m, i.sort_order), -1);
      const { error } = await supabase
        .from("tenant_gallery_items")
        .insert({ ...payload, sort_order: maxOrder + 1 });
      if (error) {
        setSaving(false);
        Swal.fire({ title: "Error", text: error.message, icon: "error", background: "#ffffff", color: "#0f172a" });
        return;
      }
    }

    setSaving(false);
    closeModal();
    fetchItems();
  };

  /* ── Delete with SweetAlert2 ── */
  const handleDelete = async (item: GalleryRow) => {
    const result = await Swal.fire({
      title: "Delete photo?",
      html: `<p style="color:rgba(255,255,255,0.5);font-size:13px">This photo will be permanently removed from your gallery.</p>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
      background: "#ffffff",
      color: "#0f172a",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#94a3b8",
    });
    if (!result.isConfirmed) return;

    setDeletingId(item.id);
    await supabase
      .from("tenant_gallery_items")
      .delete()
      .eq("id", item.id)
      .eq("tenant_id", tenantId);
    setDeletingId(null);
    fetchItems();
  };

  /* ── Toggle active (optimistic) ── */
  const handleToggle = async (item: GalleryRow) => {
    setItems((prev) =>
      prev.map((i) => i.id === item.id ? { ...i, is_active: !item.is_active } : i),
    );
    await supabase
      .from("tenant_gallery_items")
      .update({ is_active: !item.is_active })
      .eq("id", item.id)
      .eq("tenant_id", tenantId);
  };

  /* ── Move (sort_order) ── */
  const handleMove = async (id: string, dir: "up" | "down") => {
    const idx = items.findIndex((i) => i.id === id);
    if (dir === "up"   && idx === 0)               return;
    if (dir === "down" && idx === items.length - 1) return;

    setMovingId(id);
    const swapIdx = dir === "up" ? idx - 1 : idx + 1;
    const a = items[idx];
    const b = items[swapIdx];

    await Promise.all([
      supabase.from("tenant_gallery_items").update({ sort_order: b.sort_order }).eq("id", a.id).eq("tenant_id", tenantId),
      supabase.from("tenant_gallery_items").update({ sort_order: a.sort_order }).eq("id", b.id).eq("tenant_id", tenantId),
    ]);

    setMovingId(null);
    fetchItems();
  };

  const activeCount = items.filter((i) => i.is_active).length;

  /* ─── Render ──────────────────────────────────────────────── */
  return (
    <>
      <div className="space-y-5">

        {/* ── Header ── */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-slate-700 font-semibold text-sm">
              {items.length} {items.length === 1 ? "photo" : "photos"}
            </p>
            <p className="text-slate-400 text-xs">
              {activeCount} published · {items.length - activeCount} hidden
            </p>
          </div>
          <button
            onClick={openNew}
            className="flex items-center gap-2 bg-[#C8A96E] hover:bg-[#b8965a] text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-image-add-line"></i>
            Add Photo
          </button>
        </div>

        {/* ── Category filter ── */}
        {categories.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap">
            <button
              onClick={() => setActiveFilter("all")}
              className={`px-3 py-1.5 rounded-full text-xs border transition-all cursor-pointer whitespace-nowrap ${
                activeFilter === "all"
                  ? "bg-[#C8A96E] border-[#C8A96E] text-white"
                  : "bg-white border-slate-200 text-slate-500 hover:border-[#C8A96E]/40 hover:text-[#C8A96E]"
              }`}
            >
              All Photos
              <span className="ml-1.5 opacity-70">{items.length}</span>
            </button>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveFilter(cat)}
                className={`px-3 py-1.5 rounded-full text-xs border transition-all cursor-pointer whitespace-nowrap ${
                  activeFilter === cat
                    ? "bg-[#C8A96E] border-[#C8A96E] text-white"
                    : "bg-white border-slate-200 text-slate-500 hover:border-[#C8A96E]/40 hover:text-[#C8A96E]"
                }`}
              >
                {cat}
                <span className="ml-1.5 opacity-70">
                  {items.filter((i) => i.category === cat).length}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* ── Loading skeleton ── */}
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="aspect-[4/5] bg-slate-100 rounded-xl animate-pulse" />
            ))}
          </div>

        /* ── Empty state: no photos at all ── */
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 bg-white rounded-xl border border-slate-200 text-center">
            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-[#C8A96E]/10">
              <i className="ri-gallery-line text-[#C8A96E] text-2xl"></i>
            </div>
            <p className="text-slate-600 text-sm font-medium">No photos yet</p>
            <p className="text-slate-400 text-xs max-w-xs">
              Add photos to showcase your portfolio on your public website.
            </p>
            <button
              onClick={openNew}
              className="flex items-center gap-2 bg-[#C8A96E] hover:bg-[#b8965a] text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap mt-1"
            >
              <i className="ri-image-add-line"></i>
              Add First Photo
            </button>
          </div>

        /* ── Empty state: filter has no results ── */
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-12 bg-white rounded-xl border border-slate-200 text-center">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-slate-100">
              <i className="ri-filter-off-line text-slate-400 text-lg"></i>
            </div>
            <p className="text-slate-500 text-sm">No photos in &quot;{activeFilter}&quot;</p>
            <button
              onClick={() => setActiveFilter("all")}
              className="text-xs font-medium cursor-pointer transition-colors hover:underline"
              style={{ color: "#C8A96E" }}
            >
              Show all photos
            </button>
          </div>

        /* ── Grid ── */
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {filtered.map((item, idx) => {
              const realIdx   = items.findIndex((i) => i.id === item.id);
              const isDeleting = deletingId === item.id;
              const isMoving   = movingId   === item.id;

              return (
                <div
                  key={item.id}
                  className={`group relative rounded-xl overflow-hidden bg-slate-100 border transition-all ${
                    item.is_active
                      ? "border-slate-200 hover:border-[#C8A96E]/40"
                      : "border-slate-100 opacity-55 hover:opacity-75"
                  }`}
                >
                  {/* Photo */}
                  <div className="aspect-[4/5] overflow-hidden">
                    <img
                      src={item.image_url}
                      alt={item.title ?? "Gallery photo"}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.opacity = "0.3";
                      }}
                    />
                  </div>

                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-2.5">

                    {/* Top: sort arrows + status badge */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleMove(item.id, "up")}
                          disabled={realIdx === 0 || isMoving}
                          className="w-6 h-6 flex items-center justify-center rounded-md bg-black/50 text-white/80 hover:text-white hover:bg-black/70 disabled:opacity-20 cursor-pointer transition-colors"
                        >
                          <i className="ri-arrow-up-s-line text-xs"></i>
                        </button>
                        <button
                          onClick={() => handleMove(item.id, "down")}
                          disabled={realIdx === items.length - 1 || isMoving}
                          className="w-6 h-6 flex items-center justify-center rounded-md bg-black/50 text-white/80 hover:text-white hover:bg-black/70 disabled:opacity-20 cursor-pointer transition-colors"
                        >
                          <i className="ri-arrow-down-s-line text-xs"></i>
                        </button>
                      </div>
                      <span className={`px-1.5 py-0.5 rounded-md text-[9px] font-semibold ${
                        item.is_active
                          ? "bg-emerald-500/25 text-emerald-300 border border-emerald-500/30"
                          : "bg-white/10 text-white/50 border border-white/15"
                      }`}>
                        {item.is_active ? "Live" : "Hidden"}
                      </span>
                    </div>

                    {/* Bottom: meta + actions */}
                    <div>
                      {item.category && (
                        <span className="inline-block mb-1 px-2 py-0.5 rounded-full bg-[#C8A96E]/20 text-[#C8A96E] text-[9px] font-medium border border-[#C8A96E]/25">
                          {item.category}
                        </span>
                      )}
                      {item.title && (
                        <p className="text-white text-xs font-medium mb-2 line-clamp-1">{item.title}</p>
                      )}

                      {/* Action row */}
                      <div className="flex items-center gap-1.5">
                        {/* Toggle visible */}
                        <button
                          onClick={() => handleToggle(item)}
                          className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-[10px] font-medium transition-all cursor-pointer whitespace-nowrap ${
                            item.is_active
                              ? "bg-white/10 text-white/70 hover:bg-white/20"
                              : "bg-[#C8A96E]/20 text-[#C8A96E] hover:bg-[#C8A96E]/30"
                          }`}
                        >
                          <i className={item.is_active ? "ri-eye-off-line" : "ri-eye-line"}></i>
                          {item.is_active ? "Hide" : "Show"}
                        </button>

                        {/* Edit */}
                        <button
                          onClick={() => openEdit(item)}
                          title="Edit"
                          className="w-7 h-7 flex items-center justify-center rounded-lg bg-white/10 text-white/70 hover:text-white hover:bg-white/20 transition-all cursor-pointer"
                        >
                          <i className="ri-edit-2-line text-xs"></i>
                        </button>

                        {/* Delete */}
                        <button
                          onClick={() => handleDelete(item)}
                          disabled={isDeleting}
                          title="Delete"
                          className="w-7 h-7 flex items-center justify-center rounded-lg bg-red-500/15 text-red-400/70 hover:text-red-400 hover:bg-red-500/25 transition-all cursor-pointer disabled:opacity-40"
                        >
                          {isDeleting ? (
                            <div className="w-3 h-3 border border-red-400 border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <i className="ri-delete-bin-6-line text-xs"></i>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Position badge (visible when not hovering) */}
                  <div className="absolute bottom-2 left-2 w-5 h-5 flex items-center justify-center rounded-md bg-black/40 text-white/60 text-[9px] font-mono group-hover:opacity-0 transition-opacity pointer-events-none">
                    {idx + 1}
                  </div>
                </div>
              );
            })}

            {/* Add photo tile */}
            <button
              onClick={openNew}
              className="aspect-[4/5] rounded-xl border-2 border-dashed border-slate-200 hover:border-[#C8A96E]/50 hover:bg-[#C8A96E]/5 flex flex-col items-center justify-center gap-2 transition-all cursor-pointer group"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-slate-100 group-hover:bg-[#C8A96E]/10 text-slate-300 group-hover:text-[#C8A96E] transition-all">
                <i className="ri-add-line text-xl"></i>
              </div>
              <span className="text-slate-300 group-hover:text-[#C8A96E]/70 text-xs transition-colors whitespace-nowrap">
                Add Photo
              </span>
            </button>
          </div>
        )}

        {/* Public hint */}
        {items.length > 0 && (
          <p className="text-slate-400 text-xs text-center pt-1">
            <i className="ri-global-line mr-1"></i>
            Only published photos appear on your public website gallery.
          </p>
        )}
      </div>

      {/* Modal */}
      <GalleryModal
        open={modalOpen}
        editing={editing}
        saving={saving}
        onClose={closeModal}
        onSave={handleSave}
      />
    </>
  );
}